import { createWorld, defineComponent, Types, setDefaultSize, defineQuery } from 'bitecs';
import { ANIMAL_TYPES, PLANT_TYPES } from './assets/entityTypes';
import { MAX_ENTITIES } from './config.js';
import { createBeeEater } from './entities/bee_eater.js';

// Set default size for all component pools
setDefaultSize(MAX_ENTITIES);

// Create the ECS world
export const world = createWorld();
world.entities = []; // Add entities array to track entities
world.mapGenerated = false; // Track if map has been generated
world.tick = 0; // Add tick counter

// Define components
export const Position = defineComponent({
    x: Types.f32,
    y: Types.f32,
});

export const Velocity = defineComponent({
    x: Types.f32,
    y: Types.f32,
});

export const Movement = defineComponent({
    speed: Types.f32,
    acceleration: Types.f32,
    deceleration: Types.f32,
    maxSpeed: Types.f32,
    isSwimming: Types.ui8,
    isWalking: Types.ui8,
});

export const Input = defineComponent({
    up: Types.ui8,
    down: Types.ui8,
    left: Types.ui8,
    right: Types.ui8,
});

export const Sprite = defineComponent({
    texture: Types.ui32, // Reference to texture ID
});

export const Health = defineComponent({
    value: Types.f32,
});

export const Player = defineComponent(); // Tag component for player
export const Enemy = defineComponent();  // Tag component for enemies
export const Resource = defineComponent({
    type: Types.ui8, // e.g., 0 = wood, 1 = food
});

export const Inventory = defineComponent({
    wood: Types.ui32,
    food: Types.ui32,
    tools: Types.ui32,
});

export const Wetness = defineComponent({
    value: Types.f32,    // Current wetness level (0-100)
    dryingRate: Types.f32, // How fast wetness decreases
    wettingRate: Types.f32, // How fast wetness increases
});

export const Time = defineComponent({
    ticks: Types.ui32,      // Current tick count (0-24000)
    day: Types.ui32,        // Current day number
    month: Types.ui8,       // Current month (1-12)
    year: Types.ui32,       // Current year
    season: Types.ui8,      // 0=Spring, 1=Summer, 2=Autumn, 3=Winter
    isDay: Types.ui8,       // 1 if it's day, 0 if night
    brightness: Types.f32,  // Current brightness level (0-1)
});

export const Hunger = defineComponent({
    value: Types.f32,       // Current hunger level (0-100)
    drainRate: Types.f32,   // How fast hunger decreases
});

export const Thirst = defineComponent({
    value: Types.f32,       // Current thirst level (0-100)
    drainRate: Types.f32,   // How fast thirst decreases
    canDrink: Types.ui8,    // 1 if player can drink from current water source
});

// Sound level component for player
export const SoundLevel = defineComponent({
    value: Types.f32,       // Current sound level (0-100)
    decayRate: Types.f32,   // How fast sound level decreases
    isMoving: Types.ui8,    // Whether player is moving
    isRunning: Types.ui8    // Whether player is running
});

// Map components
export const Tile = defineComponent({
    type: Types.ui8, // 0 = water, 1 = land
    x: Types.ui32,   // Grid x position
    y: Types.ui32,   // Grid y position
    height: Types.f32, // Height value (0-1)
    biome: Types.ui8, // 0 = water, 1 = beach, 2 = plains, 3 = forest, 4 = mountains
});

// Tile types enum
export const TILE_TYPES = {
    WATER: 0,
    LAND: 1
};

// Biome types enum
export const BIOME_TYPES = {
    WATER: 0,
    BEACH: 1,
    PLAINS: 2,
    FOREST: 3,
    MOUNTAINS: 4
};

// Biome colors
export const BIOME_COLORS = {
    WATER: 0x1E90FF,    // Blue
    BEACH: 0xF4A460,    // Sandy brown
    PLAINS: 0x90EE90,   // Light green
    FOREST: 0x228B22,   // Forest green
    MOUNTAINS: 0x808080 // Gray
};

// Export the imported types
export { ANIMAL_TYPES, PLANT_TYPES };

// Plant component definition
export const Plant = defineComponent({
    type: Types.ui8,    // Plant type ID
    x: Types.ui32,      // Grid x position
    y: Types.ui32,      // Grid y position
    growth: Types.f32,  // Growth progress (0-1)
    harvested: Types.ui8, // Whether plant has been harvested
    foodAmount: Types.f32, // Amount of food available (0-100)
    reproductionCooldown: Types.ui32, // Ticks until next reproduction attempt
    maturityAge: Types.ui32, // Age at which plant can reproduce
    age: Types.ui32, // Current age in ticks
    energy: Types.f32, // Energy available for reproduction (0-100)
    lastReproductionTime: Types.ui32 // Last tick when reproduction occurred
});

// Animal component for basic properties
export const Animal = defineComponent({
    type: Types.ui8,     // Animal type ID
    x: Types.f32,        // Position X
    y: Types.f32,        // Position Y
    age: Types.ui32,     // Age in ticks
    weight: Types.f32,   // Current weight (0-100)
    isMoving: Types.ui8, // Whether the animal is currently moving
    energy: Types.f32,   // Current energy level
    maxEnergy: Types.f32, // Maximum energy level
    lastEnergyUpdate: Types.ui32, // Last tick when energy was updated
    fleeTimer: Types.ui32, // Timer for fleeing behavior
    isFleeing: Types.ui8, // Whether the animal is currently fleeing
    species: Types.string32 // Species name (e.g., "RABBIT", "WOLF")
});

// AI component for controlling entity behavior
export const AI = defineComponent({
    state: Types.ui8,
    targetX: Types.f32,
    targetY: Types.f32,
    speed: Types.f32,    // Movement speed
    hiveEntity: Types.eid, // Reference to the hive entity
    timer: Types.ui32, // Timer for AI state transitions
    hasNectar: Types.ui8,  // Whether the bee is carrying nectar
    workTimer: Types.ui32, // Timer for work cycle
    workDuration: Types.ui32, // Duration of work cycle
    changeDirectionTimer: Types.ui32,
    changeDirectionInterval: Types.ui32
});

// AI states enum
export const AI_STATE = {
    IDLE: 0,
    MOVING: 1,
    EATING: 2,
    RESTING: 3,
    HUNTING: 4
};

// Production component for animals that produce items
export const Production = defineComponent({
    timer: Types.ui32,   // Current production timer
    baseTime: Types.ui32, // Base time needed for production
    type: Types.ui8,
    amount: Types.f32,
    maxAmount: Types.f32,
    rate: Types.f32,
    lastProduction: Types.f32,
    cooldown: Types.f32
});

// Corpse component for dead animals
export const Corpse = defineComponent({
    type: Types.ui8,     // Type of animal that died
    x: Types.f32,        // Position X
    y: Types.f32,        // Position Y
    foodAmount: Types.f32, // Amount of food available (0-100)
    decayRate: Types.f32,  // How fast the corpse decays
    age: Types.ui32      // Age of the corpse in ticks
});

export const Rock = defineComponent({
    type: Types.ui8,    // Rock type ID
    x: Types.ui32,      // Grid x position
    y: Types.ui32,      // Grid y position
    size: Types.f32,    // Size of the rock (0-1)
});

// Rock types and their properties
export const ROCK_TYPES = {
    SMALL: {
        id: 1,
        name: 'Small Rock',
        assetName: 'rock_small',
        color: 0x808080, // Gray
        size: 0.3,      // 30% of tile size
        biomes: [1, 2, 3, 4], // All biomes except water
        description: 'A small rock'
    },
    MEDIUM: {
        id: 2,
        name: 'Medium Rock',
        assetName: 'rock_medium',
        color: 0x696969, // Dark gray
        size: 1.0,      // 50% of tile size
        biomes: [1, 2, 3, 4], // All biomes except water
        description: 'A medium-sized rock'
    },
    LARGE: {
        id: 3,
        name: 'Large Rock',
        assetName: 'rock_large',
        color: 0x404040, // Very dark gray
        size: 2.0,      // 70% of tile size
        biomes: [1, 2, 3, 4], // All biomes except water
        description: 'A large rock'
    }
};

// Wall component for structures that block light and movement
export const Wall = defineComponent({
    x: Types.ui32,      // Grid x position
    y: Types.ui32,      // Grid y position
    blocksLight: Types.ui8, // Whether the wall blocks light (1) or not (0)
    blocksMovement: Types.ui8 // Whether the wall blocks movement (1) or not (0)
});

// Wander component for entities that move randomly
export const Wander = defineComponent({
    targetX: Types.f32,
    targetY: Types.f32,
    speed: Types.f32,
    changeDirectionTimer: Types.ui32,
    changeDirectionInterval: Types.ui32
});

// Structure component for buildings and other structures
export const Structure = defineComponent({
    type: Types.ui8,
    health: Types.f32,
    maxHealth: Types.f32,
    isRepairable: Types.ui8,
    isDestructible: Types.ui8
});

// Create all queries
export const animalQuery = defineQuery([Animal, AI]);
export const tileQuery = defineQuery([Tile]);
export const plantQuery = defineQuery([Plant]);
export const playerQuery = defineQuery([Player]);
export const corpseQuery = defineQuery([Corpse]);

// Initialize the world with basic entities
export function initializeWorld(world) {
    // Note: Bee-eaters are now created after app initialization in GameEngine
    return world;
} 